import {DialogM} from "@common/components/alert/dialog/Dialog"
import {BadgeM} from "@common/components/badge/BadgeM"
import FilledButtonM from "@common/components/button/FilledButtonM"
import TouchableRippleM from "@common/components/button/TouchableRippleM"
import BannerCard from "@common/components/card/BannerCard"
import {Menu3Point} from "@common/components/menu/Menu3Point"
import TextM from "@common/components/text/TextM"
import TitleM from "@common/components/text/TitleM"
import useLiveKitEventSender from "@common/hooks/livekit/useLiveKitEventSender"
import {useApiStatus} from "@common/hooks/useApiStatus"
import useLaunchActivity from "@common/hooks/useLaunchActivity"
import {theme} from "@common/theme/theme"
import {DimensionsContext, IS_WEB} from "@common/utils/mobileUtils"
import {apiIdle, apiRequest} from "@data/redux/actions/api"
import {setTodoActivities} from "@data/redux/actions/data"
import {setIsLiveModeEnabled} from "@data/redux/actions/liveMode"
import {activitiesService} from "@data/services/activitiesService"
import {authService} from "@data/services/authService"
import {studentsService} from "@data/services/studentsService"
import {hasRole} from "@data/utility/ability"
import {isSamOpenFor} from "@data/constants/samConstants"
import {
    getActivitiesCount,
    getLastVersionPromptedForReview,
    setLastVersionPromptedForReview,
} from "@data/utility/asyncStorageUtils"
import {AbilityContext} from "@data/utility/Can"
import {findItemInListWithId, isSubscribed} from "@data/utility/dataUtils"
import {hasFeature} from "@data/utility/Utils"
import {useFocusEffect, useNavigation} from "@react-navigation/native"
import Constants from "expo-constants"
import * as StoreReview from "expo-store-review"
import moment from "moment/moment"
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState,} from "react"
import {Platform, RefreshControl, TouchableOpacity, Vibration, View} from "react-native"
import { StatusBar } from "@common/systemBars/StatusBar"
import DraggableFlatList from "react-native-draggable-flatlist"
import {Swipeable} from "react-native-gesture-handler"
import {ActivityIndicator, IconButton} from "react-native-paper"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import {connect} from "react-redux"
import SelfCreateActivityButton from "../SelfCreateActivityButton"
import {getTodaysFinishedActivities} from "../../../data/utility/dataUtils"
import GenerateActivityModal from "../../../tutor/students/GenerateActivityModal"
import {findClosestNextOccurrence} from "../../account/onboarding/LiveRoomCard"
import {ChampionImageM} from "../../champions/ChampionImageM"
import RealizationsListItem from "../../organize/list/RealizationsListItem"
import ChaptersList from "./ChaptersList"
import NextLiveSessionBanner from "./NextLiveSessionBanner"
import TodoModal, {canEditActivity} from "./TodoModal"
import NextAssessmentWidget from "../../schoolAssessments/NextAssessmentWidget"
import {schoolAssessmentsService} from "@data/services/schoolAssessmentsService"
import EditActivityTitleModal from "./EditActivityTitleModal"

// Le même écran est monté tel quel dans l'espace parent : l'élève est tutoyé,
// le parent est vouvoyé et on parle de l'activité, pas de « ta » liste.
const TEXTS = {
    student: {
        archiveManyHint: "Tu pourras les restaurer depuis les activités archivées.",
        archiveDenied: "L'activité a été créée par ton tuteur, tu ne peux pas l'archiver.",
        archiveOneTitle: "Veux-tu archiver cette activité ?",
        archiveOneHint: "Tu pourras la restaurer depuis les activités archivées.",
        emptyTitle: "Lance une activité !",
        emptySubtitle: "Ta liste est vide pour l'instant...",
    },
    parent: {
        archiveManyHint: "Elles pourront être restaurées depuis les activités archivées.",
        archiveDenied: "L'activité a été créée par le tuteur, elle ne peut pas être archivée.",
        archiveOneTitle: "Voulez-vous archiver cette activité ?",
        archiveOneHint: "Elle pourra être restaurée depuis les activités archivées.",
        emptyTitle: "Lancez une activité",
        emptySubtitle: "La liste est vide pour l'instant...",
    },
}

const TodoListScreen = (props) => {
    // ** Component props
    const { student, todoList = null, itemStyle = {}, nav } = props

    const texts = TEXTS[props.isParentMode ? "parent" : "student"]

    const byChapter = false

    const [showFinishedActivitiesList, setShowFinishedActivitiesList] =
        useState(false)
    const [showTodoActivitiesList, setShowTodoActivitiesList] = useState(true)

    const navigation = nav ?? useNavigation()

    const request = student
        ? props.getStudentRequest
        : props.getUserRequest ||
        props.getSessionsRequest ||
        props.getCallRequest
    const getUserService = student
        ? studentsService.getStudent
        : authService.getUser
    const getUserParams = student ? { id: student?.id } : {}

    useEffect(() => {
        if (student) props.apiRequest(getUserService, getUserParams)
    }, [student?.id])

    const promptStoreReviewIsApplicable = async () => {
        const isAvailable = await StoreReview.isAvailableAsync()
        const hasAction = await StoreReview.hasAction()
        if (isAvailable && hasAction) {
            const count = await getActivitiesCount()
            if (count >= 1) {
                const lastVersionPromptedForReview =
                    await getLastVersionPromptedForReview()

                if (
                    lastVersionPromptedForReview !==
                    Constants.expoConfig.version
                ) {
                    return true
                }
            }
        }
        return false
    }

    useFocusEffect(
        useCallback(() => {
            if (hasRole(props.user, "student")) {
                setShowFinishedActivitiesList(false)
                let isActive = true
                if (Platform.OS !== "web")
                    promptStoreReviewIsApplicable().then((isApplicable) => {
                        if (isApplicable)
                            setTimeout(() => {
                                if (isActive) {
                                    setLastVersionPromptedForReview().then()
                                    StoreReview.requestReview().catch(
                                        (error) => {
                                            console.error("error", error)
                                        }
                                    )
                                }
                            }, 2000)
                    })
                return () => {
                    isActive = false
                }
            }
        }, [])
    )

    // Refresh user data when screen regains focus (handles stale data after app backgrounded overnight)
    // Skip first focus — BottomTabsNavigator already fetches on initial mount
    const isFirstFocus = useRef(true)
    useFocusEffect(
        useCallback(() => {
            if (!student) {
                if (isFirstFocus.current) {
                    isFirstFocus.current = false
                    return
                }
                props.apiRequest(authService.getUser)
            }
        }, [])
    )

    const user = student ? student : props.user
    const draggableFlatListRef = useRef()
    const chaptersListRef = useRef()
    const todos = todoList ?? user?.todo_activities
    const [isSwiped, setIsSwiped] = useState(false)
    const [selectionMode, setSelectionMode] = useState(false)
    const [selectedActivities, setSelectedActivities] = useState([])

    // Selection mode functions
    const toggleSelectionMode = () => {
        setSelectionMode(!selectionMode)
        setSelectedActivities([])
    }

    const handleActivitySelection = (activityId) => {
        if (selectedActivities.includes(activityId)) {
            setSelectedActivities(
                selectedActivities.filter((id) => id !== activityId)
            )
        } else {
            setSelectedActivities([...selectedActivities, activityId])
        }
    }

    const handleDeleteMultiple = () => {
        if (selectedActivities.length === 0) return

        DialogM.show({
            champion: "zoe",
            text1: `Archiver ${selectedActivities.length} activité${
                selectedActivities.length > 1 ? "s" : ""
            } ?`,
            text2: texts.archiveManyHint,
            buttonText1: "Archiver",
            buttonColor1: theme.colors.primary,
            onPress: () => {
                const serviceToUse = student
                    ? activitiesService.deleteActivitiesTodo
                    : activitiesService.deleteActivitiesTodo
                const params = student ? { student: student?.id } : {}
                props.apiRequest(
                    serviceToUse,
                    params,
                    { activity_ids: selectedActivities }
                )
                toggleSelectionMode()
            },
            buttonText2: "Annuler",
        })
    }

    const finishedActivities = useMemo(
        () => getTodaysFinishedActivities(user),
        [user]
    )

    // If no activity in todo_activities or activities which contains "Devoir"
    const isStep1 = user?.profile?.parent_call_requested === null
    const isStep2 =
        user?.xp === 0 &&
        !user?.todo_activities?.find((todo) =>
            todo?.material?.title
                ?.toLowerCase()
                .includes("demande pour la séance")
        ) &&
        !user?.activities?.find((activity) =>
            activity?.material?.title
                ?.toLowerCase()
                .includes("demande pour la séance")
        )

    const closestNextLiveSessionOccurrence = findClosestNextOccurrence(
        user?.live_sessions
    )
    const liveMoment = moment(closestNextLiveSessionOccurrence?.date)
    const isToday = liveMoment?.isSame(moment(), "day")
    const isPast = liveMoment?.isBefore(moment())

    const showCompleteProfileBanner =
        hasRole(props.user, "student") &&
        !props.isParentMode &&
        (isStep1 || isStep2) &&
        !isSubscribed(user) &&
        user?.subscription_state?.status === "on_generic_trial" &&
        Math.abs(moment().diff(moment(user?.trial_ends_at), "days")) <= 7 &&
        !props.isLiveMode &&
        (!isToday || isPast) // Show the banner if the live session is not today or is in the past

    const dimensions = useContext(DimensionsContext)
    const [isOpen, setOpen] = useState(false)
    const [selectedItemId, setSelectedItemId] = useState(null)
    const [showGenerateModal, setShowGenerateModal] = useState(false)
    const [isTitleEditModalVisible, setTitleEditModalVisible] = useState(false)
    const [selectedActivityForTitleEdit, setSelectedActivityForTitleEdit] = useState(null)
    const handleShowModal = () => setOpen(!isOpen)
    const selectedItem = findItemInListWithId(selectedItemId, todos)
        ?? findItemInListWithId(selectedItemId, finishedActivities)
    const schoolSubject = findItemInListWithId(
        selectedItem?.school_subject_id,
        props.schoolSubjects
    )

    const { sendLiveKitData: sendNewTodo } = useLiveKitEventSender()

    useApiStatus(
        activitiesService.activityTodoIndexesAsStudent,
        null,
        true,
        (data) => {
            if (props.isLiveMode) {
                sendNewTodo(
                    "newTodo",
                    {
                        id: student?.id,
                        activity: data,
                    },
                    [student?.id]
                )
            }
        }
    )

    useApiStatus(
        activitiesService.activityTodoIndexesAsTutor,
        null,
        true,
        () => {
            if (props.isLiveMode) {
                sendNewTodo(
                    "newTodo",
                    {
                        id: student?.id,
                    },
                    [student?.id]
                )
            }
        }
    )

    useApiStatus(activitiesService.deleteActivitiesTodo, null, true, (data) => {
        if (props.isLiveMode) {
            sendNewTodo(
                "deleteTodo",
                {
                    id: student?.id,
                    activity_ids: data?.deleted_activity_ids || [],
                },
                [student?.id]
            )
        }
    })

    const ability = useContext(AbilityContext)
    const onDeleteTodo = (todoId) => {
        const activity = findItemInListWithId(todoId, todos)
        const canEdit = canEditActivity(activity, ability)

        if (!canEdit) {
            DialogM.show({
                text1: "Non autorisé",
                text2: texts.archiveDenied,
            })
            return
        }
        setPendingAction("delete")
        props.apiRequest(activitiesService.getActivity, { id: todoId })
        if (props.getActivitySuccess)
            DialogM.show({
                text1: texts.archiveOneTitle,
                text2: texts.archiveOneHint,
                buttonText1: "Archiver",
                buttonColor1: theme.colors.primary,
                onPress: () => {
                    props.apiRequest(
                        activitiesService.deleteActivitiesTodo,
                        { student: student?.id ?? props.user?.id },
                        { activity_ids: [todoId] }
                    )
                },
                buttonText2: "Annuler",
            })
    }
    const onUpdatePress = (todoId) => {
        const activity = findItemInListWithId(todoId, todos)
        const canEdit = canEditActivity(activity, ability)
        if (!canEdit) {
            DialogM.show({
                text1: "Non autorisé",
                text2: "L'activité a été créée par ton tuteur, tu ne peux pas la modifier.",
            })
            return
        }
        setPendingAction("update")
        props.apiRequest(activitiesService.getActivity, { id: todoId })
    }

    const onEditTitlePress = (todoId) => {
        const activity = findItemInListWithId(todoId, todos)
        const canEdit = canEditActivity(activity, ability)
        if (!canEdit) {
            DialogM.show({
                text1: "Non autorisé",
                text2: "L'activité a été créée par ton tuteur, tu ne peux pas la modifier.",
            })
            return
        }
        setSelectedActivityForTitleEdit(activity)
        setTitleEditModalVisible(true)
    }
    let row = []
    let prevOpenedRow
    const [pendingAction, setPendingAction] = useState(null)

    const onPressShowInfo = (item) => {
        navigation.push("activity-presentation", {
            activityTemplateId: item?.activity_template_id,
            activityId: item?.id,
            schoolSubjectId: item?.school_subject_id,
            studentId: item?.student_id,
            chapterId: item?.student_chapter?.id,
            chapterName: item?.student_chapter?.chapter?.name,
            ongoing: item?.student_chapter?.ongoing,
            xpGained: item?.student_chapter?.xp_gained,
            lastFeelingSentAt: item?.student_chapter?.last_feeling_sent_at,
        })
    }
    useApiStatus(
        activitiesService.getActivity,
        null,
        true,
        (data) => {
            if (data?.started_at && data?.student_id !== props.user?.id) {
                DialogM.show({
                    text1: "Impossible",
                    text2: "L'activité a déjà été commencée par l'élève il y a moins d'une heure.",
                })
            } else {
                if (pendingAction === "delete") {
                    onDeleteTodo(data?.id)
                } else if (pendingAction === "update") {
                    navigation?.push("new-todo", {
                        todoId: data?.id,
                        studentId: student ? student?.id : null,
                    })
                }
            }
            setPendingAction(null)
        },
        () => {
            handleShowModal()
            setPendingAction(null)
        }
    )

    const launchActivity = useLaunchActivity()

    const renderItem = ({ item, drag, isActive }) => {
        const schoolSubject = findItemInListWithId(
            item?.school_subject_id,
            props.schoolSubjects
        )
        const color = schoolSubject?.color
        const isSelected = selectedActivities.includes(item?.id)

        const closeRow = (index) => {
            if (prevOpenedRow && prevOpenedRow !== row[index]) {
                prevOpenedRow.close()
            }
            prevOpenedRow = row[index]
            setIsSwiped(false)
        }

        const renderRightActions = (progress, dragX, onClick, item) => {
            // Don't show action buttons in parent mode
            if (props.isParentMode) {
                return null
            }

            return (
                <View
                    style={{
                        flexDirection: "row",
                        paddingRight: dimensions?.width > 800 ? 60 : 0,
                    }}
                >
                    <IconButton
                        onPress={() => onEditTitlePress(item?.id)}
                        iconColor={theme.colors.light}
                        containerColor={theme.colors.transparent}
                        icon={"edit"}
                        style={{
                            marginRight: 10,
                            marginBottom: 15,
                            alignSelf: "center",
                        }}
                        size={17}
                    />
                    {props.getActivityRequest && pendingAction === "delete" ? (
                        <View
                            style={{
                                width: 40,
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <ActivityIndicator color={theme.colors.primary} />
                        </View>
                    ) : (
                         <IconButton
                            onPress={onClick}
                            iconColor={theme.colors.light}
                            containerColor={theme.colors.transparent}
                            icon={"archive"}
                            style={{
                                marginRight: 10,
                                marginBottom: 15,
                                alignSelf: "center",
                            }}
                            size={17}
                        />)

                    }
                </View>
            )
        }
        const itemContent = (
            <RealizationsListItem
                activity={item}
                onPressSwipe={
                    selectionMode
                        ? undefined
                        : () => {
                            if (!isSwiped) {
                                row[item?.id]?.openRight()
                            } else {
                                row[item?.id]?.close()
                            }
                        }
                }
                custom
                student={student}
                dragButton={selectionMode ? null : drag}
                onLongPress={
                    selectionMode 
                        ? undefined 
                        : (hasRole(props.user, "tutor") || props.isParentMode)
                        ? () => {
                            setSelectionMode(true)
                            handleActivitySelection(item?.id)
                        }
                        : drag
                }
                disabled={isActive}
                todo={true}
                selected={isSelected}
                selectionMode={selectionMode}
                color={theme.colors.error}
                onPress={
                    selectionMode
                        ? () => handleActivitySelection(item?.id)
                        : () => {
                            if (!isSwiped || !IS_WEB) {
                                setSelectedItemId(item?.id)
                                // In parent mode, always show modal
                                // Otherwise, if user is student, launch activity, else show modal
                                if (props.isParentMode) {
                                    handleShowModal()
                                } else if (hasRole(props.user, "student")) {
                                    // Off-live: eligible students open todo
                                    // activities with Sam. In live mode, or when
                                    // the student isn't Sam-eligible, we keep the
                                    // historical activity flow. Même règle que
                                    // la bannière (`isSamOpenFor`).
                                    if (
                                        props.isLiveMode ||
                                        !isSamOpenFor(props.user)
                                    ) {
                                        setPendingAction("start")
                                        launchActivity(item)
                                    } else {
                                        navigation.push("sam-session", {
                                            activityId: item?.id,
                                        })
                                    }
                                } else {
                                    handleShowModal()
                                }
                            }
                        }
                }
                style={itemStyle}
            />
        )

        if (selectionMode) {
            return (
                <TouchableOpacity
                    onPress={() => handleActivitySelection(item?.id)}
                    disabled={isActive}
                    style={{
                        width: dimensions?.width - 20,
                        alignSelf: "center",
                    }}
                >
                    {itemContent}
                </TouchableOpacity>
            )
        }

        return (
            <Swipeable
                renderRightActions={
                    hasRole(props.user, "student")
                        ? null
                        : (progress, dragX) => (
                            <>
                                {renderRightActions(
                                    progress,
                                    dragX,
                                    () => onDeleteTodo(item?.id),
                                    item
                                )}
                            </>
                        )
                }
                containerStyle={{
                    width: dimensions?.width,
                }}
                onSwipeableClose={() => closeRow(item?.id)}
                ref={(ref) => (row[item?.id] = ref)}
                rightOpenValue={props.isParentMode ? 0 : -200}
                enabled={!props.isParentMode}
            >
                {itemContent}
            </Swipeable>
        )
    }

    const onPressNewTodo = () => {
        if (todos?.length < props.maxTodoActivities)
            navigation?.push("new-todo", {
                studentId: student ? student?.id : null,
            })
        else
            DialogM.show({
                champion: "zoe",
                text1: `Tu ne peux pas dépasser ${props.maxTodoActivities} activités dans ta liste`,
                text2: "C'est l'heure de faire un peu de ménage ! Supprime les activités qui ne sont plus utiles.",
            })
    }

    const goToStep = () => {
        if (isStep1) {
            navigation.push("trial-school-subjects")
        } else if (isStep2) {
            navigation.push("trial-preparation-form")
        }
    }

    function renderListHeaderComponent() {
        return (
            !todoList && (
                <>
                    {showCompleteProfileBanner && (
                        <BannerCard
                            title={`Complétez les informations de ${props.user?.firstname}`}
                            message={"Pour préparer au mieux la séance d'essai"}
                            buttonLabel="Compléter mon profil"
                            onButtonClick={goToStep}
                        />
                    )}
                    {!props.isParentMode && (
                        <NextLiveSessionBanner showLivesButton={false} />
                    )}
                    {!props.isParentMode && (
                        <NextAssessmentWidget studentId={student?.id} />
                    )}
                    <View
                        style={{
                            flexDirection: "row",
                            alignItems: "center",
                            alignSelf: "center",
                            justifyContent: "space-between",
                            width: dimensions?.width,
                            borderColor: theme.colors.lightMore,
                            paddingVertical: 15,
                            paddingHorizontal: 10,
                        }}
                    >
                        <View>
                            <TitleM fontWeight="ExtraBold">
                                {!byChapter ? "ACTIVITÉS" : "PAR CHAPITRE"}
                            </TitleM>
                        </View>

                        <View
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                            }}
                        >
                            {!todoList && (
                                <>
                                    {request && IS_WEB ? (
                                        <ActivityIndicator
                                            color={theme.colors.primary}
                                            size={"small"}
                                        />
                                    ) : (
                                        <IconButton
                                            onPress={() => {
                                                props.apiRequest(
                                                    getUserService,
                                                    getUserParams
                                                )
                                            }}
                                            iconColor={theme.colors.primary}
                                            containerColor={
                                                theme.colors.transparent
                                            }
                                            disabled={request}
                                            icon={"sync-alt"}
                                            size={20}
                                        />
                                    )}
                                </>
                            )}
                            {(hasRole(props.user, "tutor") || props.isParentMode) && (
                                <Menu3Point
                                menuItems={[
                                    {
                                        title: "Activités archivées",
                                        icon: "archive",
                                        onPress: () =>
                                            navigation.navigate("archived-activites", {
                                                studentId: student?.id,
                                            }),
                                    },
                                ]}
                                color={theme.colors.primary}
                                iconSize={20}
                                style={{ marginLeft: 8 }}
                            />
                            )}
                        </View>
                    </View>
                    {(finishedActivities?.length > 0 || user?.xp > 0) && (
                        <View>
                            <View
                                style={{
                                    backgroundColor: theme.colors.lighter,
                                    marginHorizontal: 10,
                                    borderRadius: 10,
                                    width: dimensions?.width - 30,
                                    alignSelf: "center",
                                }}
                            >
                                {(finishedActivities?.length > 0 ||
                                    user?.xp > 0) && (
                                    <TouchableOpacity
                                        onPress={() => {
                                            setShowFinishedActivitiesList(
                                                !showFinishedActivitiesList
                                            )
                                        }}
                                        style={{
                                            flexDirection: "row",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            paddingHorizontal: 8,
                                            paddingVertical: 8,
                                            borderRadius: 6,
                                            marginRight: 5,
                                            marginLeft: 5,
                                            marginTop: 5,
                                            marginBottom: 5,
                                        }}
                                    >
                                        <View
                                            style={{
                                                flexDirection: "row",
                                                alignItems: "center",
                                            }}
                                        >
                                            <TextM
                                                fontSize={12}
                                                color={theme.colors.grey}
                                                fontWeight="Bold"
                                            >
                                                FAITES
                                            </TextM>
                                            <BadgeM
                                                style={{
                                                    backgroundColor:
                                                    theme.colors.lighter,
                                                    color: theme.colors.grey,
                                                    marginLeft: 5,
                                                }}
                                                count={
                                                    finishedActivities?.length
                                                }
                                                size={16}
                                            />
                                        </View>
                                        <TextM
                                            fontSize={10}
                                            color={theme.colors.grey}
                                            fontWeight="Bold"
                                            style={{
                                                textDecorationLine: "underline",
                                            }}
                                        >
                                            {showFinishedActivitiesList
                                                ? "MASQUER"
                                                : "AFFICHER"}
                                        </TextM>
                                    </TouchableOpacity>
                                )}
                                {showFinishedActivitiesList && (
                                    <View
                                        style={{
                                            alignSelf: "center",
                                            width: "100%",
                                        }}
                                    >
                                        {finishedActivities.map((activity) => (
                                            <RealizationsListItem
                                                key={activity.id}
                                                activity={activity}
                                                custom
                                                student={student}
                                                todo={hasRole(props.user, "tutor") || props.isParentMode}
                                                onPress={hasRole(props.user, "tutor") || props.isParentMode ? () => {
                                                    setSelectedItemId(activity?.id)
                                                    handleShowModal()
                                                } : undefined}
                                                style={{
                                                    opacity: 0.8,
                                                    width:
                                                        dimensions?.width - 40,
                                                    alignSelf: "center",
                                                }}
                                            />
                                        ))}
                                        {!showFinishedActivitiesList ? (
                                            <></>
                                        ) : (
                                            <View style={{}}>
                                                <FilledButtonM
                                                    mode={"text"}
                                                    color={theme.colors.primary}
                                                    style={{
                                                        width: null,
                                                        marginBottom: 0,
                                                    }}
                                                    labelStyle={{
                                                        fontSize: 10,
                                                        height: 16,
                                                    }}
                                                    contentStyle={{
                                                        height: 30,
                                                    }}
                                                    onPress={() => {
                                                        navigation.push(
                                                            "realizations",
                                                            {
                                                                initialTabRealizations: true,
                                                                withGoBack: true,
                                                                ...(student
                                                                    ? {
                                                                        studentId:
                                                                        student?.id,
                                                                    }
                                                                    : {}),
                                                            }
                                                        )
                                                    }}
                                                    label={"Voir plus"}
                                                />
                                            </View>
                                        )}
                                    </View>
                                )}
                            </View>
                            <TouchableOpacity
                                onPress={() => {
                                    setShowTodoActivitiesList(
                                        !showTodoActivitiesList
                                    )
                                }}
                                disabled
                                style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    paddingHorizontal: 8,
                                    paddingVertical: 8,
                                    borderRadius: 6,
                                    marginRight: 15,
                                    marginLeft: 15,
                                    marginBottom: 5,
                                    borderColor: theme.colors.lighter,
                                    paddingTop: 20,
                                    marginTop: -5,
                                    width: dimensions?.width - 30,
                                    alignSelf: "center",
                                }}
                            >
                                <View
                                    style={{
                                        flexDirection: "row",
                                        alignItems: "center",
                                    }}
                                >
                                    {selectionMode ? (
                                        <TouchableOpacity
                                            onPress={toggleSelectionMode}
                                            style={{
                                                flexDirection: "row",
                                                alignItems: "center",
                                                paddingVertical: 4,
                                                borderRadius: 4,
                                            }}
                                        >
                                            <FontAwesome5
                                                name="times"
                                                size={16}
                                                color={theme.colors.primary}
                                                style={{ marginRight: 8 }}
                                            />
                                            <TextM
                                                fontSize={12}
                                                color={theme.colors.primary}
                                                fontWeight="Bold"
                                            >
                                                {selectedActivities.length} élément{selectedActivities.length > 1 ? 's' : ''} sélectionné{selectedActivities.length > 1 ? 's' : ''}
                                            </TextM>
                                        </TouchableOpacity>
                                    ) : (
                                        <>
                                            <TextM
                                                fontSize={12}
                                                color={theme.colors.primary}
                                                fontWeight="Bold"
                                            >
                                                À FAIRE
                                            </TextM>
                                            <BadgeM
                                                style={{
                                                    backgroundColor:
                                                    theme.colors.primaryLighter,
                                                    color: theme.colors.primary,
                                                    marginLeft: 5,
                                                }}
                                                count={todos?.length ?? "0"}
                                                size={16}
                                            />
                                        </>
                                    )}
                                </View>
                                {(hasRole(props.user, "tutor") || props.isParentMode) && todos?.length > 0 && (
                                        <TouchableOpacity
                                            onPress={() => {
                                                if (
                                                    !selectionMode
                                                ) {
                                                    setSelectionMode(
                                                        true
                                                    )
                                                    const allActivityIds =
                                                        todos?.map(
                                                            (
                                                                activity
                                                            ) =>
                                                                activity.id
                                                        ) || []
                                                    setSelectedActivities(
                                                        allActivityIds
                                                    )
                                                } else {
                                                    // Toggle select all/none
                                                    if (
                                                        selectedActivities.length ===
                                                        todos?.length
                                                    ) {
                                                        setSelectedActivities(
                                                            []
                                                        )
                                                    } else {
                                                        const allActivityIds =
                                                            todos?.map(
                                                                (
                                                                    activity
                                                                ) =>
                                                                    activity.id
                                                            ) || []
                                                        setSelectedActivities(
                                                            allActivityIds
                                                        )
                                                    }
                                                }
                                            }}
                                            style={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: 4,
                                                backgroundColor:
                                                    selectionMode &&
                                                    selectedActivities.length ===
                                                    todos?.length
                                                        ? theme
                                                            .colors
                                                            .error
                                                        : theme
                                                            .colors
                                                            .white,
                                                alignItems:
                                                    "center",
                                                justifyContent:
                                                    "center",
                                                borderWidth:
                                                    selectionMode &&
                                                    selectedActivities.length ===
                                                    todos?.length
                                                        ? 0
                                                        : 2,
                                                borderColor:
                                                theme.colors
                                                    .lightMore,
                                            }}
                                        >
                                            {selectionMode &&
                                                selectedActivities.length ===
                                                todos?.length && (
                                                    <FontAwesome5
                                                        name="check"
                                                        size={10}
                                                        color={
                                                            theme
                                                                .colors
                                                                .white
                                                        }
                                                    />
                                                )}
                                        </TouchableOpacity>
                                    )}

                            </TouchableOpacity>
                            <FontAwesome5
                                name={
                                    showTodoActivitiesList
                                        ? "chevron-up"
                                        : "chevron-down"
                                }
                                size={12}
                                color={theme.colors.primary}
                                style={{ marginLeft: 5, opacity: 0 }}
                            />
                        </View>
                    )}
                </>
            )
        )
    }
    const isStudentSelfCreate =
        hasRole(props.user, "student") && !props.isParentMode && !student
    const materialGenerationAvailable =
        hasRole(props.user, "tutor") || isStudentSelfCreate

    const canOpenGenerateModal = props.isParentMode || materialGenerationAvailable

    const renderListEmptyComponent = () => {
        return (
            <View
                style={{
                    alignItems: "center",
                    justifyContent: "center",
                    paddingHorizontal: 20,
                    paddingVertical: 20,
                    flex: 1,
                    alignSelf: "center",
                }}
            >
                <ChampionImageM name="zoe" variant={"main"} width={90} />
                {canOpenGenerateModal ? (
                    <View>
                        <TitleM
                            fontWeight="ExtraBold"
                            style={{
                                textAlign: "center",
                                marginTop: 10,
                                color: theme.colors.grey,
                            }}
                            wrap
                        >
                            {texts.emptyTitle}
                        </TitleM>
                        <TextM
                            fontWeight="Medium"
                            style={{ textAlign: "center" }}
                            wrap
                        >
                            {texts.emptySubtitle}
                        </TextM>
                    </View>
                ) : (
                    <TitleM
                        fontWeight="ExtraBold"
                        style={{
                            textAlign: "center",
                            marginTop: 10,
                            color: theme.colors.grey,
                        }}
                        wrap
                    >
                        Tu n'as pas d'activité à faire
                    </TitleM>
                )}
            </View>
        )
    }


    const onPress = () => {
        if (canOpenGenerateModal) {
            setShowGenerateModal(true)
        } else {
            onPressNewTodo()
        }
    }

    const renderListFooterComponent = () => {
        return (
            !todoList && !selectionMode && (
                <View
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                        paddingHorizontal: 10,
                    }}
                >
                    {(hasRole(props.user,"tutor") || props.isParentMode) && todos?.length > 0 && (
                        <TouchableRippleM
                            onPress={toggleSelectionMode}
                            style={{
                                height: 50,
                                width: 50,
                                backgroundColor: theme.colors.light,
                                borderRadius: 25,
                                marginRight: 10,
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <FontAwesome5
                                name="archive"
                                size={20}
                                color={theme.colors.white}
                            />
                        </TouchableRippleM>
                    )}
                    {canOpenGenerateModal && (
                        <SelfCreateActivityButton onPress={onPress} />
                    )}
                </View>
            )
        )
    }

    return (
        <View
            style={{
                flex: 1,
                alignSelf: "center",
            }}
        >
            {byChapter ? (
                <ChaptersList
                    todos={todos}
                    schoolSubjects={props.schoolSubjects}
                     navigation={navigation}
                    chaptersListRef={chaptersListRef}
                    listHeaderComponent={renderListHeaderComponent}
                    listEmptyComponent={renderListEmptyComponent}
                    ListFooterComponent={renderListFooterComponent}
                />
            ) : (
                <DraggableFlatList
                    data={showTodoActivitiesList ? todos ?? [] : []}
                    containerStyle={{
                        flex: 1,
                    }}
                    contentContainerStyle={{
                        alignItems: "center",
                        paddingBottom: 20,
                    }}
                    refreshControl={
                        <RefreshControl
                            onRefresh={() => {
                                props.apiRequest(getUserService, getUserParams)
                                const studentId = student?.id ?? props.user?.id
                                if (studentId) {
                                    props.apiRequest(
                                        schoolAssessmentsService.getSchoolAssessments,
                                        {},
                                        {},
                                        { student_id: studentId, upcoming: true }
                                    )
                                }
                            }}
                            refreshing={!!request}
                        />
                    }
                    ListHeaderComponent={renderListHeaderComponent}
                    ListFooterComponent={renderListFooterComponent}
                    ref={draggableFlatListRef}
                    onDragBegin={() => Vibration.vibrate(80)}
                    onDragEnd={({ data }) => {
                        Vibration.vibrate(80)
                        props.setTodoActivities(data)
                        if (hasRole(props.user, "student"))
                            props.apiRequest(
                                activitiesService.activityTodoIndexesAsStudent,
                                {},
                                {
                                    todo_activities_ids: data.map(
                                        (todo) => todo.id
                                    ),
                                }
                            )
                        else
                            props.apiRequest(
                                activitiesService.activityTodoIndexesAsTutor,
                                { id: student?.id },
                                {
                                    todo_activities_ids: data.map(
                                        (todo) => todo.id
                                    ),
                                }
                            )
                    }}
                    keyExtractor={(item) => item?.id}
                    ListEmptyComponent={
                        showTodoActivitiesList ? renderListEmptyComponent : null
                    }
                    renderItem={renderItem}
                />
            )}

            {selectionMode && (
                <View
                    style={{
                        paddingVertical: 8,
                        paddingHorizontal: 20,
                        alignSelf: "center",
                        width: '100%',
                    }}
                >
                    <FilledButtonM
                        color={theme.colors.error}
                        style={{
                            maxWidth: 400,
                            alignSelf: 'center',
                        }}
                        textColor={theme.colors.white}
                        onPress={handleDeleteMultiple}
                        disabled={
                            selectedActivities.length === 0 ||
                            props.deleteActivitiesTodoRequest
                        }
                        loading={
                            props.deleteActivitiesTodoRequest
                        }
                        icon="archive"
                        label={`Archiver (${selectedActivities.length})`}
                    />
                </View>
            )}

            {isOpen && (
                <TodoModal
                    isOpen={isOpen}
                    handleShowModal={handleShowModal}
                    item={selectedItem}
                    itemId={selectedItemId}
                    navigation={navigation}
                    onDeleteTodo={onDeleteTodo}
                    studentId={student?.id}
                    schoolSubject={schoolSubject}
                />
            )}
            {showGenerateModal && (
                <GenerateActivityModal
                    isOpen={showGenerateModal}
                    onClose={() => setShowGenerateModal(false)}
                    student={student || props.user}
                    schoolSubjects={props.schoolSubjects}
                    selfCreate={isStudentSelfCreate}
                />
            )}
            <EditActivityTitleModal
                isVisible={isTitleEditModalVisible}
                onClose={() => {
                    setTitleEditModalVisible(false)
                    setSelectedActivityForTitleEdit(null)
                }}
                activity={selectedActivityForTitleEdit}
            />
            <StatusBar
                style="dark"
                backgroundColor={theme.colors.white}
                translucent={false}
            />
        </View>
    )
}

const mapStateToProps = (state) => {
    return {
        activityTodoIndexesAsStudentRequest:
        state.api.activityTodoIndexesAsStudent?.request,
        activityTemplates: state.data.staticData?.activity_templates,
        user: state.data.currentUser?.object,
        schoolSubjects: state.data.staticData?.school_subjects,
        getUserRequest: state.api.getUser?.request,
        getStudentRequest: state.api.getStudent?.request,
        isLive: state.liveMode.isLiveModeEnabled,
        liveSessions: state.data.currentUser?.object?.live_sessions,
        liveSessionId: state.liveMode?.liveSessionId,
        currentLiveSession: state.data.currentUser?.object?.live_sessions?.find(
            (x) => x?.id === state.liveMode?.liveSessionId
        ),
        inCallUsers: state.liveMode?.inCallUsers,
        getLiveSessionSuccess: state.api.getLiveSession?.success,
        callPortal: state.liveMode?.callPortal,
        maxTodoActivities: state.data.staticData?.max_todo_activities,
        isLiveMode: state.liveMode.isLiveModeEnabled,
        getActivityRequest: state.api.getActivity?.request,
        getActivitySuccess: state.api.getActivity?.success,
        updatedActivityEventData: state.activity?.updatedActivityEventData,
        isParentMode: state.data.family?.isParentMode,
        deleteActivitiesTodoRequest: state.api.deleteActivitiesTodo?.request,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
    setTodoActivities,
    setIsLiveModeEnabled,
}

export default connect(mapStateToProps, mapDispatchToProps)(TodoListScreen)
